import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { subscribeStomp, ensureConnected } from "../stompClient";
import { normalizeWsStatus } from "./wsFrameNormalizer";
import { PDA_PREFIX } from "../../global/constants";
import { persistJob, removePersistedJob } from "./JobPersistenceService";

const FRAME_TIMEOUT_MS = 30_000; // start polling if no frame received in 30s
const POLL_INTERVAL_MS = 8_000;  // poll every 8s

// Map<jobId, cleanupFn> — allows N concurrent jobs of the same kind.
const _activeJobs = new Map();

function asNumber(value) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

export function getProgressCounts(frame = {}) {
  const total =
    asNumber(frame.total) ??
    asNumber(frame.totalProjects) ??
    asNumber(frame.totalItems) ??
    asNumber(frame.totalCount) ??
    asNumber(frame.totalFiles);
  const completed =
    asNumber(frame.completedCount) ??
    asNumber(frame.completed) ??
    asNumber(frame.successCount) ??
    asNumber(frame.processedCount);
  const failed =
    asNumber(frame.failedCount) ??
    asNumber(frame.failed) ??
    asNumber(frame.errorCount);
  return { total, completed, failed };
}

export function getTerminalFromCounts(frame, currentStatus, requireItemTerminal = true) {
  if (requireItemTerminal && currentStatus !== "COMPLETED" && currentStatus !== "FAILED") return null;
  const { total, completed, failed } = getProgressCounts(frame);
  if (total == null || total <= 0) return null;
  const done = (completed ?? 0) + (failed ?? 0);
  if (done < total) return null;
  return (failed ?? 0) >= total && (completed ?? 0) === 0
    ? "ALL_FAILED"
    : "ALL_COMPLETED";
}

// Mirrors importActions.readEtaSeconds — coalesces the various ETA field names
// the backend may emit into a finite number, or null when none is present.
export function readEtaSeconds(frame = {}) {
  return (
    asNumber(frame.etaSecondsRemaining) ??
    asNumber(frame.estimatedRemainingSeconds) ??
    asNumber(frame.remainingSeconds) ??
    asNumber(frame.etaSeconds)
  );
}

/**
 * Core utility for all async WS-backed actions.
 *
 * Critical ordering guarantee:
 *   1. Connection established (ensureConnected)
 *   2. Subscribe to topic          ← BEFORE the POST
 *   3. Fire POST (backend starts processing)
 *   4. Parse 202 body (jobId, topic)
 *   5. Re-subscribe if backend provides a job-scoped topic
 *   6. onJobStarted({ jobId, topic }) so caller dispatches STARTED
 *   7. Arm 30s no-frame timeout → polling fallback
 *
 * Step 2 happens before step 3 so early frames (STARTED, QUEUED) emitted
 * immediately after the 202 are never missed. The previous implementation
 * subscribed after the POST, which dropped those early frames and left
 * progress cards frozen on "Calculating".
 *
 * jobId is always a non-null string:
 *   - If the backend 202 body carries a jobId → use it (enables polling)
 *   - Otherwise → generate a client-side UUID (no polling, but multi-job
 *     Redux keying still works across concurrent same-kind actions)
 *
 * @param {object} config
 *   postFn         - () => Promise<AxiosResponse>  triggers the backend job
 *   fallbackTopic  - user-scoped topic to subscribe to before the POST
 *   kind           - job kind string (for logging)
 * @param {object} callbacks
 *   onJobStarted({ jobId, topic })
 *   onQueued(frame)
 *   onItemStarted(frame)
 *   onItemCompleted(frame)
 *   onItemFailed(frame)
 *   onFinalizing(frame)
 *   onAllCompleted(frame)
 *   onAllFailed(frame)
 *   onRequestError(err)
 *
 * @returns cleanup() — cancels only this job's subscription
 */
export function startWsJob(config, callbacks = {}) {
  const { postFn, fallbackTopic, frameFilter } = config;

  const {
    onJobStarted = () => {},
    onQueued = () => {},
    onItemStarted = () => {},
    onItemCompleted = () => {},
    onItemFailed = () => {},
    onFinalizing = () => {},
    onAllCompleted = () => {},
    onAllFailed = () => {},
    onRequestError = () => {},
  } = callbacks;

  // jobId is set synchronously to a client UUID, then overridden by the 202
  // body if the backend sends one. It is always non-null so Redux slices
  // always have a valid jobs[jobId] key, even before the 202 arrives.
  let jobId = uuidv4();
  let hasBackendJobId = false; // only true if 202 body carried a jobId
  let topic = fallbackTopic;
  let unsubscribeFn = null;
  let frameTimeoutId = null;
  let pollingController = null;
  let _finalized = false;
  // ALL_COMPLETED / ALL_FAILED frames received before the 202 is processed:
  // they may belong to a concurrent job on the same topic, so they wait for
  // our backend jobId instead of finalizing this card blind.
  let responseProcessed = false;
  const heldTerminalFrames = [];

  function finalize(cb, frame) {
    if (_finalized) return;
    _finalized = true;
    clearTimeout(frameTimeoutId);
    if (pollingController) pollingController.abort();
    if (unsubscribeFn) {
      unsubscribeFn();
      _activeJobs.delete(jobId);
    }
    // Remove from persistence — job is done, no need to restore on refresh.
    if (hasBackendJobId) removePersistedJob(jobId);
    cb(frame);
  }

  function startPollingFallback() {
    if (_finalized || !hasBackendJobId || pollingController) return;
    pollingController = new AbortController();
    startJobPolling(jobId, pollingController.signal, {
      onAllCompleted: (frame) => finalize(onAllCompleted, frame),
      onAllFailed: (frame) => finalize(onAllFailed, frame),
    });
  }

  function armFrameTimeout() {
    // Only poll if the backend provided a real jobId (client UUIDs have no
    // matching /async-job/{jobId}/status entry on the server).
    if (_finalized || !hasBackendJobId) return;
    clearTimeout(frameTimeoutId);
    frameTimeoutId = setTimeout(() => {
      startPollingFallback();
    }, FRAME_TIMEOUT_MS);
  }

  function buildHandler() {
    return (message) => {
      let frame;
      try { frame = JSON.parse(message.body); } catch { return; }
      handleFrame(frame);
    };
  }

  function handleFrame(frame) {
    // jobId filtering: only active once hasBackendJobId is true (i.e. the 202
    // has been fully processed and `jobId` has been updated to the backend UUID).
    //
    // IMPORTANT: do NOT filter before hasBackendJobId is set. Early frames
    // (STARTED, QUEUED) are emitted by the server-side async thread before
    // the HTTP 202 response travels back to the browser. At that point the
    // handler's `jobId` closure still holds the client-generated UUID while
    // frame.jobId already carries the backend UUID → unconditional filtering
    // would drop QUEUED, leaving the card stuck on "0/? Calculating…" forever.
    if (hasBackendJobId && frame.jobId && frame.jobId !== jobId) return;
    if (typeof frameFilter === "function" && !frameFilter(frame)) return;

    const status = normalizeWsStatus(frame.status);
    if (!responseProcessed && (status === "ALL_COMPLETED" || status === "ALL_FAILED")) {
      heldTerminalFrames.push(frame);
      return;
    }

    if (pollingController) {
      pollingController.abort();
      pollingController = null;
    }
    armFrameTimeout();

    switch (status) {
      case "STARTED":
        // Server-side STARTED frame — no action needed, QUEUED carries the data.
        break;
      case "QUEUED":
        onQueued(frame);
        break;
      case "PROJECT_STARTED":
        onItemStarted(frame);
        break;
      case "COMPLETED":
        onItemCompleted(frame);
        {
          const terminalStatus = getTerminalFromCounts(frame, status);
          if (terminalStatus === "ALL_FAILED") finalize(onAllFailed, frame);
          if (terminalStatus === "ALL_COMPLETED") finalize(onAllCompleted, frame);
        }
        break;
      case "FAILED":
        onItemFailed(frame);
        {
          const terminalStatus = getTerminalFromCounts(frame, status);
          if (terminalStatus === "ALL_FAILED") finalize(onAllFailed, frame);
          if (terminalStatus === "ALL_COMPLETED") finalize(onAllCompleted, frame);
        }
        break;
      case "FINALIZING":
        // Post-items phase (e.g. capacity reset's atomic swap): the bar is
        // full but the job is still writing — carries its own ETA.
        onFinalizing(frame);
        break;
      case "ALL_COMPLETED":
        finalize(onAllCompleted, frame);
        break;
      case "ALL_FAILED":
        finalize(onAllFailed, frame);
        break;
      default:
        break;
    }
  }

  // ─── Execution flow ──────────────────────────────────────────────────────

  // Notify the caller up front (BEFORE awaiting the socket) so the Redux slice
  // has jobs[jobId] ready immediately. This guarantees the progress card exists
  // even if the connection times out — onRequestError can then mark THAT card
  // as failed instead of the error being silently dropped (the caller's error
  // dispatch keys by jobId, which would be null if onJobStarted hadn't fired).
  onJobStarted({ jobId, topic });

  // Persist immediately with the client UUID so the card survives a refresh
  // even if the backend 202 body never arrives. Migrated to the backend UUID
  // in step 4 if one is provided; dropped in the catch if the request fails.
  if (config.kind) {
    persistJob({ jobId, kind: config.kind, topic, startedAt: Date.now() });
  }

  ensureConnected()
    .then(() => {
      // Subscribe on the fallback topic BEFORE firing the POST so early frames
      // (STARTED, QUEUED) are never missed.
      unsubscribeFn = subscribeStomp(fallbackTopic, buildHandler());
      _activeJobs.set(jobId, cleanup);

      // Fire the POST to trigger the backend job.
      return postFn();
    })
    .then((response) => {
      // Step 4: Parse the 202 body.
      // The jobId was already communicated to the Redux slice (step 2) using
      // the client-side UUID. The backend jobId, if present, is stored only
      // for use by the polling fallback — the Redux key stays the client UUID.
      const body = response?.data;
      if (body?.jobId) {
        hasBackendJobId = true;
        const backendJobId = body.jobId;
        _activeJobs.set(backendJobId, cleanup);

        // If the backend assigned a job-scoped topic, re-subscribe.
        const newTopic = body.topic || fallbackTopic;
        if (newTopic !== fallbackTopic) {
          unsubscribeFn?.();
          topic = newTopic;
          unsubscribeFn = subscribeStomp(topic, buildHandler());
        }

        // Override jobId for frame filtering and polling with the real one.
        const oldClientId = jobId;
        jobId = backendJobId;

        // Migrate localStorage: replace client UUID entry with backend UUID so
        // the polling endpoint (/async-job/{backendJobId}/status) can be used
        // on the next refresh instead of just re-subscribing to the WS topic.
        removePersistedJob(oldClientId);
        if (config.kind) {
          persistJob({ jobId: backendJobId, kind: config.kind, topic, startedAt: Date.now() });
        }
      }

      // Replay terminal frames that raced the 202: handleFrame now drops
      // those carrying another job's id (all are kept without a backend id).
      responseProcessed = true;
      heldTerminalFrames.splice(0).forEach(handleFrame);

      // Step 5: Arm polling fallback (only if backend provided a real jobId).
      armFrameTimeout();
    })
    .catch((err) => {
      heldTerminalFrames.length = 0;
      if (unsubscribeFn) { unsubscribeFn(); _activeJobs.delete(jobId); }
      // The job was persisted before the POST fired; a failed request means it
      // never ran — drop it or rehydration resurrects a phantom card.
      removePersistedJob(jobId);
      onRequestError(err);
    });

  function cleanup() {
    if (unsubscribeFn) {
      unsubscribeFn();
      _activeJobs.delete(jobId);
    }
    clearTimeout(frameTimeoutId);
    if (pollingController) pollingController.abort();
  }

  return cleanup;
}

/**
 * Polls GET /async-job/{jobId}/status until terminal or signal aborted.
 * Only called when the backend 202 body provided a real jobId.
 */
export function startJobPolling(jobId, signal, callbacks = {}) {
  // onPending: the server answered and the job is not terminal yet.
  // trustCounts=false: settle on the terminal status only — for jobs whose
  // outcome (`result`) is written after the last item completes.
  const {
    onAllCompleted = () => {},
    onAllFailed = () => {},
    onPending = () => {},
    trustCounts = true,
  } = callbacks;

  async function poll() {
    if (signal.aborted) return;
    try {
      const resp = await axios.get(`${PDA_PREFIX}/async-job/${jobId}/status`);
      const payload = resp.data ?? {};
      const status = normalizeWsStatus(payload.status);
      // FINALIZING says every item is done and the job is still writing (capacity
      // reset: minutes of row swap). Its counts read total/total, so the count rule
      // closed the card as done and dropped the socket before the real terminal frame.
      const terminalFromCounts =
        status === "FINALIZING" || !trustCounts ? null : getTerminalFromCounts(payload, status, false);
      if (status === "ALL_COMPLETED" || terminalFromCounts === "ALL_COMPLETED") {
        onAllCompleted(payload);
        return;
      }
      if (status === "ALL_FAILED" || terminalFromCounts === "ALL_FAILED") {
        onAllFailed({ ...payload, error: payload.error });
        return;
      }
      onPending(payload);
    } catch (err) {
      // 404 → the backend has no record of this job (never registered, or
      // purged). It can never reach a terminal state, so stop polling and
      // resolve the card as failed instead of looping forever.
      if (err?.response?.status === 404) {
        onAllFailed({ error: "This job is no longer tracked by the server." });
        return;
      }
      // Other errors (network / 5xx) are transient — keep polling.
    }
    if (!signal.aborted) {
      setTimeout(poll, POLL_INTERVAL_MS);
    }
  }

  setTimeout(poll, POLL_INTERVAL_MS);
}
